import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Grid,
  Button,
  ButtonBase,
  LinearProgress,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import ManagerEditModeToggle from '../shared/ManagerEditModeToggle';
import RaffleEditSheet from '../shared/RaffleEditSheet';
import VoidRaffleDialog from '../shared/VoidRaffleDialog';
import RaffleAuditMode from './RaffleAuditMode';
import { TOKENS, fmtMoney } from '../theme';
import { localDecisionsSelector } from '../../../redux/selectors/employeeSessionV2';
import { recordLocalDecision } from '../../../redux/actions/employeeSessionV2';

// Spec §5 — Raffle Report. List view with dual-photo thumbnails + audit
// tracker. Manager Edit Mode hides the tracker and surfaces edit/void.
// There is no "add" — under the Raffle 1 spec a winning number only ever
// comes from the server draw on the Raffle screen.
export default function RaffleDetail({
  session,
  sessionData,
  customers,
  onEditRaffle,
  onDeleteRaffle,
  onSubmitDecision,
  onAuditComplete,
  // Hide the Manager Edit Mode toggle for verification-only surfaces (e.g.
  // the reading guy auditing from Master Reading).
  hideEditMode = false,
}) {
  const dispatch = useDispatch();
  const [editMode, setEditMode] = useState(false);
  const [editSheet, setEditSheet] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [auditIndex, setAuditIndex] = useState(0);
  const [auditOpen, setAuditOpen] = useState(false);

  const decisions = useSelector(localDecisionsSelector);

  const raffles = useMemo(
    () => (sessionData?.raffles || []).filter((r) => r.status !== 'deleted'),
    [sessionData]
  );
  const total = useMemo(
    () => raffles.reduce((s, r) => s + Number(r.amount || 0), 0),
    [raffles]
  );

  const raffleDecisions = Object.entries(decisions)
    .filter(([k]) => k.startsWith('RAFFLE:'))
    .reduce((acc, [k, v]) => {
      acc[k] = v;
      return acc;
    }, {});
  const reviewedCount = Object.keys(raffleDecisions).length;
  const approvedCount = Object.values(raffleDecisions).filter(
    (d) => d === 'APPROVED'
  ).length;
  const flaggedCount = Object.values(raffleDecisions).filter(
    (d) => d === 'FLAGGED'
  ).length;
  const remainingCount = raffles.length - reviewedCount;
  const auditComplete = reviewedCount === raffles.length && raffles.length > 0;

  const openAudit = (idx) => {
    setAuditIndex(idx);
    setAuditOpen(true);
  };
  const handleDecision = (raffle, decision) => {
    dispatch(recordLocalDecision('RAFFLE', raffle.raffleId, decision));
    onSubmitDecision(raffle, decision);
  };
  const handleAuditComplete = () => {
    setAuditOpen(false);
    if (onAuditComplete) onAuditComplete('Raffle audit completed');
  };

  const handleSaveEdit = (payload) => {
    onEditRaffle(editSheet.entry, payload);
    setEditSheet(null);
  };
  // Raffle 1 spec R54 — a raffle is voided with a reason, never deleted.
  const handleConfirmVoid = (reason) => {
    if (!confirmDelete) return;
    onDeleteRaffle(confirmDelete, reason);
    setConfirmDelete(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, py: 1.5 }}>
      {!hideEditMode && (
        <ManagerEditModeToggle
          on={editMode}
          onToggle={() => setEditMode((m) => !m)}
        />
      )}

      {/* Hero */}
      <Box
        sx={{
          background: '#fff',
          border: `1px solid ${TOKENS.pink200}`,
          borderRadius: 2,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: TOKENS.slate500,
            }}
          >
            Total Raffle
          </Typography>
          <Typography
            sx={{
              fontSize: 28,
              fontWeight: 700,
              fontFamily: 'monospace',
              color: TOKENS.slate900,
            }}
          >
            {fmtMoney(total)}
          </Typography>
          <Typography sx={{ fontSize: 11, color: TOKENS.slate500, mt: 0.25 }}>
            {raffles.length} winners
          </Typography>
        </Box>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: TOKENS.pink50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <EmojiEventsIcon sx={{ color: TOKENS.pink700, fontSize: 24 }} />
        </Box>
      </Box>

      {!editMode && raffles.length > 0 && (
        <Box
          sx={{
            background: auditComplete ? TOKENS.emerald50 : '#fff',
            border: `1px solid ${
              auditComplete ? TOKENS.emerald300 : TOKENS.slate200
            }`,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: auditComplete
                    ? TOKENS.emerald600
                    : TOKENS.slate900,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {auditComplete ? (
                  <CheckIcon sx={{ color: '#fff', fontSize: 16 }} />
                ) : (
                  <VisibilityIcon sx={{ color: '#fff', fontSize: 16 }} />
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                  {auditComplete
                    ? 'Identity verification complete'
                    : 'Identity verification required'}
                </Typography>
                <Typography sx={{ fontSize: 11, color: TOKENS.slate500 }}>
                  {reviewedCount} of {raffles.length} verified ·{' '}
                  {remainingCount} remaining
                </Typography>
              </Box>
            </Box>
            <LinearProgress
              variant="determinate"
              value={(reviewedCount / Math.max(1, raffles.length)) * 100}
              sx={{
                height: 8,
                borderRadius: 4,
                mb: 1,
                background: TOKENS.slate200,
                '& .MuiLinearProgress-bar': { background: TOKENS.emerald500 },
              }}
            />
            <Grid container spacing={1}>
              <Stat
                label="Approved"
                value={approvedCount}
                color={TOKENS.emerald700}
              />
              <Stat
                label="Flagged"
                value={flaggedCount}
                color={TOKENS.red700}
              />
              <Stat
                label="Remaining"
                value={remainingCount}
                color={TOKENS.slate700}
              />
            </Grid>
          </Box>
          <Button
            fullWidth
            onClick={() =>
              openAudit(
                Math.max(
                  0,
                  raffles.findIndex(
                    (r) => !raffleDecisions[`RAFFLE:${r.raffleId}`]
                  )
                )
              )
            }
            endIcon={<ChevronRightIcon />}
            sx={{
              py: 1.5,
              borderRadius: 0,
              background: auditComplete ? TOKENS.emerald700 : TOKENS.slate900,
              color: '#fff',
              justifyContent: 'space-between',
              px: 2,
              '&:hover': {
                background: auditComplete ? TOKENS.emerald900 : TOKENS.slate700,
              },
            }}
          >
            {reviewedCount === 0
              ? 'Start Verification'
              : auditComplete
              ? 'Review Verification'
              : 'Continue Verification'}
          </Button>
        </Box>
      )}

      {/* Raffle cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {raffles.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              color: TOKENS.slate400,
              fontSize: 13,
              background: '#fff',
              border: `1px solid ${TOKENS.slate200}`,
              borderRadius: 2,
            }}
          >
            No raffles for this session.
          </Box>
        )}
        {raffles.map((r, idx) => {
          const dec = raffleDecisions[`RAFFLE:${r.raffleId}`];
          const borderColor =
            dec === 'APPROVED'
              ? TOKENS.emerald300
              : dec === 'FLAGGED'
              ? TOKENS.red300
              : TOKENS.slate200;
          const idPhoto = r.photo?.[0]?.localURL;
          const validationPhoto = r.files?.[0]?.localURL;
          return (
            <Box
              key={r.raffleId}
              sx={{
                background: '#fff',
                border: `1px solid ${borderColor}`,
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <ButtonBase
                onClick={() =>
                  editMode
                    ? setEditSheet({ mode: 'edit', entry: r, index: idx + 1 })
                    : openAudit(idx)
                }
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1.25,
                  textAlign: 'left',
                }}
              >
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  <PhotoThumb
                    src={idPhoto}
                    label="ID"
                    border={TOKENS.blue200}
                    bg={TOKENS.blue600}
                  />
                  <PhotoThumb
                    src={validationPhoto}
                    label="VAL"
                    border={TOKENS.emerald200}
                    bg={TOKENS.emerald600}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 9,
                          fontWeight: 700,
                          color: TOKENS.slate400,
                          fontFamily: 'monospace',
                        }}
                      >
                        #{idx + 1}
                      </Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }} noWrap>
                        {r.name}
                      </Typography>
                    </Box>
                    {editMode ? (
                      <EditIcon sx={{ color: TOKENS.amber600, fontSize: 14 }} />
                    ) : dec ? (
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          background:
                            dec === 'APPROVED'
                              ? TOKENS.emerald600
                              : TOKENS.red600,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {dec === 'APPROVED' ? (
                          <CheckIcon sx={{ color: '#fff', fontSize: 12 }} />
                        ) : (
                          <CloseIcon sx={{ color: '#fff', fontSize: 12 }} />
                        )}
                      </Box>
                    ) : null}
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mt: 0.5,
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.25,
                        }}
                      >
                        <AccessTimeIcon
                          sx={{ fontSize: 11, color: TOKENS.slate500 }}
                        />
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontFamily: 'monospace',
                            color: TOKENS.slate500,
                          }}
                        >
                          {r.time}
                        </Typography>
                      </Box>
                      {r.winnerNumber && (
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontFamily: 'monospace',
                            fontWeight: 700,
                            color: TOKENS.pink700,
                          }}
                        >
                          # {r.winnerNumber}
                        </Typography>
                      )}
                      {/* Which raffle the number came out of. With three
                          types running at one location, "# 10012" and
                          "# 1112" are otherwise indistinguishable, and a
                          manager auditing the shift cannot tell a ticket
                          from an auto number. */}
                      {(r.drawingName || r.mode === 'machine') && (
                        <Typography
                          sx={{ fontSize: 10, color: TOKENS.slate500 }}
                        >
                          · {r.mode === 'machine' ? 'Machine' : r.drawingName}
                          {r.colorName ? ` · ${r.colorName}` : ''}
                        </Typography>
                      )}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        fontFamily: 'monospace',
                        color: TOKENS.pink700,
                      }}
                    >
                      ${r.amount}
                    </Typography>
                  </Box>
                </Box>
              </ButtonBase>
              {editMode && (
                <Box
                  sx={{
                    px: 1.25,
                    py: 0.5,
                    borderTop: `1px solid ${TOKENS.slate100}`,
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button
                    size="small"
                    startIcon={<BlockIcon />}
                    onClick={() => setConfirmDelete(r)}
                    sx={{ color: TOKENS.red600, fontSize: 11 }}
                  >
                    Void
                  </Button>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {editSheet && (
        <RaffleEditSheet
          // Force remount each open so useState(entry.X)
          // re-initialises — otherwise the Drawer's close
          // transition keeps stale state from the previous edit.
          key={editSheet.entry?.raffleId}
          open
          onClose={() => setEditSheet(null)}
          entry={editSheet.entry}
          index={editSheet.index}
          customers={customers}
          onSave={handleSaveEdit}
        />
      )}
      {confirmDelete && (
        <VoidRaffleDialog
          open
          onClose={() => setConfirmDelete(null)}
          onConfirm={handleConfirmVoid}
          title={`Void ${
            confirmDelete.raffleNo
              ? `Raffle #${confirmDelete.raffleNo}`
              : `Raffle Entry #${raffles.indexOf(confirmDelete) + 1}`
          }?`}
          subtitle={`${confirmDelete.name} · ${confirmDelete.amount}`}
          entry={confirmDelete}
        />
      )}
      <RaffleAuditMode
        open={auditOpen}
        onClose={() => setAuditOpen(false)}
        onComplete={handleAuditComplete}
        raffles={raffles}
        decisions={decisions}
        auditIndex={auditIndex}
        onIndexChange={setAuditIndex}
        onDecision={handleDecision}
      />
    </Box>
  );
}

function PhotoThumb({ src, label, border, bg }) {
  return (
    <Box
      sx={{
        width: 56,
        height: 56,
        borderRadius: 1,
        overflow: 'hidden',
        background: TOKENS.slate100,
        border: `2px solid ${border}`,
        position: 'relative',
      }}
    >
      {src && (
        <Box
          component="img"
          src={src}
          alt={label}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          background: bg + 'CC',
          color: '#fff',
          fontSize: 7,
          fontWeight: 700,
          textAlign: 'center',
          py: 0.25,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </Box>
    </Box>
  );
}

function Stat({ label, value, color }) {
  return (
    <Grid item xs={4}>
      <Box
        sx={{
          textAlign: 'center',
          background: TOKENS.slate50,
          borderRadius: 1,
          py: 0.75,
        }}
      >
        <Typography
          sx={{
            fontSize: 15,
            fontWeight: 700,
            fontFamily: 'monospace',
            color,
            lineHeight: 1,
          }}
        >
          {value}
        </Typography>
        <Typography
          sx={{
            fontSize: 8,
            fontWeight: 700,
            textTransform: 'uppercase',
            color: TOKENS.slate500,
            mt: 0.25,
          }}
        >
          {label}
        </Typography>
      </Box>
    </Grid>
  );
}
